import colors, {emerald, neutral, rose, yellow} from "tailwindcss/colors"
import {spacing} from "tailwindcss/defaultTheme"

// TODO get spacing from runtime config
const extendedSpacing = {
    ...spacing,
    'xxs': spacing['2'],
    'xs': spacing['3'],
    'sm': spacing['4'],
    'md': spacing['5'],
    'lg': spacing['6'],
    'xl': spacing['7'],
    'xxl': spacing['8']
}

const extendedColors = {
    ...colors,
    danger: colors.rose,
    success: colors.emerald,
    warning: colors.yellow,
    info: colors.neutral
}

/**
 * Styler class to build styles based on Tailwind CSS conventions.
 *
 * It allows for dynamic styling based on existing classes and provides methods to set text color, background color, and sizing.
 *
 * Usage:
 * ```javascript
 * import { useStyles } from '@/composables/useStyles'
 * const { builder, definedSize } = useStyles()
 * const styles = builder
 *   .watchFor('text-gray-500 bg-blue-100 w-32 h-32')
 *   .textColor('blue', 600)
 *   .backgroundColor('gray', 200)
 *   .sizing('md')
 *   .build()
 *
 *   // This will generate a style object that can be applied to a component.
 *   console.log(styles)
 *   * // Output: { color: '#2563eb', backgroundColor: '#e5e7eb', width: '1.25rem', height: '1.25rem' }
 *
 *   * // Check if a size is defined
 *   console.log(definedSize('md')) // true
 *   console.log(definedSize('xxl')) // false
 * ```
 */
class Styler {
    color?: string = null
    background?: string = null
    width?: string = null
    height?: string = null
    preExistingClass?: string = null

    get() {
        const classes = this.preExistingClass?.split(' ') ?? []
        const textColorClassExists = classes.some(c => c.startsWith('text-'))
        const backgroundColorClassExists = classes.some(c => c.startsWith('bg-'))
        const sizingClassExists = classes.some(c => c.startsWith('size-') || c.startsWith('w-') || c.startsWith('h-'))

        const style = {}

        if (this.color && !textColorClassExists)
            style['color'] = this.color
        if (this.background && !backgroundColorClassExists)
            style['backgroundColor'] = this.background
        if (this.width && !sizingClassExists)
            style['width'] = this.width
        if (this.height && !sizingClassExists)
            style['height'] = this.height

        return style
    }

    watchFor(classes: string) {
        this.preExistingClass = classes
        return this
    }

    textColor(color?: string, value: number = 500) {
        this.color = this.colorize(color, value)
        return this
    }

    backgroundColor(color?: string, value: number = 500) {
        this.background = this.colorize(color, value)
        return this
    }

    colorize(color?: string, value: number = 500) {
        if (!color) return null
        if (extendedColors[color]) return extendedColors[color][value] || extendedColors[color].DEFAULT || extendedColors[color]
        const moduleColor = $getModuleColor(color)
        if (moduleColor) return moduleColor[value] || moduleColor.DEFAULT || moduleColor
        return null
    }

    sizing(size?: string = null) {
        if (extendedSpacing[size]) {
            this.width = extendedSpacing[size]
            this.height = extendedSpacing[size]
        }
        return this
    }

    widen(size: string = null) {
        if (extendedSpacing[size]) {
            this.width = extendedSpacing[size]
        }
        return this
    }

    heighten(size: string = null) {
        if (extendedSpacing[size]) {
            this.height = extendedSpacing[size]
        }
        return this
    }

    definedColor(color?: string = null) {
        return !!extendedColors[color] || !!$getModuleColor(color)
    }

    definedSize(size?: string = null) {
        return !!extendedSpacing[size]
    }
}

export const useStyles = () => {
    return {
        build: () => new Styler(),
        definedColor: new Styler().definedColor,
        definedSize: new Styler().definedSize,
    }
}
